import { AbstractControl, ValidationErrors } from '@angular/forms';
// REV-18627: konkrete Pfade statt Barrel — zirkulaerer Import in Node (siehe contract-C1.md)
import { EMPTY_ARRAY_LENGTH } from '../../../../aumico-lib-core-fe/src/lib/constants/constants';
import { NumberUtils } from '../../../../aumico-lib-core-fe/src/lib/utils/number-utils';
import {
    AccountType,
    FinancialStatementTypes,
    IAccount,
    IAccountFilter,
    IBalanceSwitchAccount,
    IFinancialStatementSource,
} from '@modeso/types__aumico-lib-annualreport-be';
import Debug from 'debug';
import { cloneDeep, differenceBy } from 'lodash-es';
import { BalanceSwitchAccountService } from '../../../../aumico-lib-templates-fe/src/lib/services/balance-switch-account.service';
import { ISheetRow } from '../interfaces/ISheetRow';
import { ISpreadSheet } from '../interfaces/ISpreadSheet';
import { RawAccountsHelper } from './rawAccounts.helper';

const LOWER_BOUNDARY_INDEX_AFTER_RANGE_SPLIT = 0;
const UPPER_BOUNDARY_INDEX_AFTER_RANGE_SPLIT = 1;
const debug = Debug('aumico:aumico-lib-annualreports-fe:SwitchAccounts');

interface ILocalAccount extends IAccount {
    isSummarized?: boolean;
    summarizedFrom?: ILocalAccount[];
}

interface SummarizedAccountInfo {
    accountNumber: string;
    accountName: string;
}

export class FinancialStatementImportFormHelper {
    /**
     * Helper to determine if an account should be shown based on user-defined filters.
     * * CRITICAL FIX: The logic ensures that if an account is from a previous annual report, 
     * it MUST still satisfy the account number filter. For standard accounts, ALL 
     * active filters must be satisfied.
     */
    public static isMatchesFilter(filter: IAccountFilter, account: IAccount): boolean {
        // If no filter is provided, everything matches
        if (this.isEmptyObject(filter)) {
            return true;
        }

        const isAccountNoWithinRange = FinancialStatementImportFormHelper.isWithinRange(
            filter.accountNumber,
            account.accountNumber,
        );

        // If the account comes from a previous annual report, we only enforce the account number filter.
        // This is usually because historical data integrity is prioritized over name-based or value-based filtering.
        if (account.comingFromPreviousAnnualReport) {
            return isAccountNoWithinRange;
        }

        // For standard accounts parsed from the sheet, evaluate all filter criteria.
        const isAccountNameWithinRange = FinancialStatementImportFormHelper.isWithinRange(
            filter.accountName,
            account.name,
        );
        const isOpeningValueWithinRange = FinancialStatementImportFormHelper.isWithinRange(
            filter.openingValue,
            account.openingBalance,
        );
        const isClosingValueWithinRange = FinancialStatementImportFormHelper.isWithinRange(
            filter.closingValue,
            account.closingBalance,
        );
        const isWordIncluded = FinancialStatementImportFormHelper.hasSubstring(filter.includedWord, account.name);
        const isWordExcluded = FinancialStatementImportFormHelper.hasNoSubstring(filter.excludedWord, account.name);
        const isAccountWithNoZeroBalance = FinancialStatementImportFormHelper.hasNoZeroBalance(
            filter.removeAccountWithZeroBalance,
            account.openingBalance,
            account.closingBalance,
        );

        return (
            isAccountNameWithinRange &&
            isAccountNoWithinRange &&
            isOpeningValueWithinRange &&
            isClosingValueWithinRange &&
            isWordIncluded &&
            isWordExcluded &&
            isAccountWithNoZeroBalance
        );
    }

    /**
     * Helper to check if an object is effectively empty (only contains null or empty strings).
     */
    public static isEmptyObject(object: object): boolean {
        if (!object) {
            return true;
        }
        const keys = Object.keys(object);
        if (keys.length === 0) {
            return true;
        }
        return keys.every((key) => object[key] === '' || object[key] == null);
    }

    /**
     * Core logic for extracting accounts from a spreadsheet and reconciling with previous year data.
     */
    public static getAccountsFromSpreadsheet(
        financialStatementSource: IFinancialStatementSource,
        noPreviousYearData: boolean,
        isPreviousYearDataTakenFromPreviousAnnualReport: boolean,
        previousAccounts: IAccount[],
        previousBalanceSwitchAccounts: IBalanceSwitchAccount[],
        spreadSheetData: ISpreadSheet,
        accountType: AccountType,
        startRow: number,
        endRow: number,
        priorYearUnmatchableAccountsDetected: boolean,
        currentSummarizedAccountsSet: Set<string>,
    ): IAccount[] {
        let finalAccounts: IAccount[] = [];
        const sheetName: string = financialStatementSource.importDefinition.sheetName.name;
        const foundSheet = spreadSheetData.sheets.find((spreadSheet) => spreadSheet.name === sheetName);

        if (foundSheet == null) {
            return [];
        }

        // Create a map to store rows by their row number for easy access later
        const rowMap = new Map<number, ISheetRow>();

        // --- Step 1: First pass to get raw values from the sheet ---
        const rawAccounts: ILocalAccount[] = [];
        const accountNumberColNo = Number(financialStatementSource.importDefinition.accountNoCol.id);
        const accountNameColNo = Number(financialStatementSource.importDefinition.accountNameCol.id);

        const openingColNo: number | null =
            noPreviousYearData || isPreviousYearDataTakenFromPreviousAnnualReport || financialStatementSource.importDefinition.previousYearColNo == null
                ? null
                : Number(financialStatementSource.importDefinition.previousYearColNo.id);

        const openingColNoForDuplicateAccounts: number | null =
            !priorYearUnmatchableAccountsDetected || financialStatementSource.importDefinition.previousYearColNo == null
                ? null
                : Number(financialStatementSource.importDefinition.previousYearColNo.id);

        const closingColNo = Number(financialStatementSource.importDefinition.balanceColNo.id);
        const shareholderColNo = Number(
            financialStatementSource.importDefinition.shareholderColNo != null
                ? financialStatementSource.importDefinition.shareholderColNo.id
                : null,
        );
        // REV-18627 (B6/B9): Kontenzweck-Spalte, nur im Zweck-Format gesetzt.
        const purposeColNo: number | null =
            financialStatementSource.importDefinition.purposeColNo != null
                ? Number(financialStatementSource.importDefinition.purposeColNo.id)
                : null;

        const indexingColumnIndex = 1;
        const OneBased = 1;
        const indexingRowIndex = 1;
        const startRowIndex = startRow - indexingRowIndex;
        const endRowIndex = endRow - indexingRowIndex;

        const accountNumberCellIndex = accountNumberColNo - indexingColumnIndex - OneBased;
        const accountsStartingRowIndex =
            financialStatementSource.importDefinition.accountsStartingRow - indexingRowIndex;
        const accountNameColumnCellIndex = accountNameColNo - indexingColumnIndex - OneBased;
        const accountOpeningValueCellIndex = openingColNo != null ? openingColNo - indexingColumnIndex - OneBased : -1;
        const accountOpeningValueCellIndexForDuplicateAccounts =
            openingColNoForDuplicateAccounts != null
                ? openingColNoForDuplicateAccounts - indexingColumnIndex - OneBased
                : -1;
        const accountClosingValueCellIndex = closingColNo - indexingColumnIndex - OneBased;
        const shareholderCellIndex = shareholderColNo - indexingColumnIndex - OneBased;
        const purposeCellIndex = purposeColNo != null ? purposeColNo - indexingColumnIndex - OneBased : -1;

        const rawAccountsHelper = new RawAccountsHelper(
            noPreviousYearData,
            isPreviousYearDataTakenFromPreviousAnnualReport,
            financialStatementSource,
        );

        foundSheet.rows.forEach((row: ISheetRow, index: number) => {
            const isWithinDataRange = index >= accountsStartingRowIndex;
            const withinAccountsRange = index >= startRowIndex && index <= endRowIndex;
            const hasNoRange = startRow == null || endRow == null;

            // Map the row number (1-based) to the row object
            rowMap.set(index + 1, row);

            if (isWithinDataRange && (withinAccountsRange || hasNoRange) && row.cells.length != EMPTY_ARRAY_LENGTH) {
                const closingBalance = rawAccountsHelper.getAccountClosingBalanceValue(
                    accountClosingValueCellIndex,
                    row,
                );
                let accountNumber = row.cells[accountNumberCellIndex];
                if (accountNumber == null) {
                    accountNumber = '';
                }

                let accountName = row.cells[accountNameColumnCellIndex] ?? '';
                const shareholder = row.cells[shareholderCellIndex];
                const purpose = purposeCellIndex >= 0 ? row.cells[purposeCellIndex] ?? undefined : undefined;

                let openingBalance = 0;
                if (!isPreviousYearDataTakenFromPreviousAnnualReport && accountOpeningValueCellIndex >= 0) {
                    const openingBalanceValue = row.cells[accountOpeningValueCellIndex];
                    if (NumberUtils.isFinite(openingBalanceValue)) {
                        openingBalance = NumberUtils.roundNumber(openingBalanceValue);
                    }
                }

                const isRowEmpty =
                    accountNumber === '' &&
                    accountName === '' &&
                    (shareholder == null || shareholder === '') &&
                    NumberUtils.isZero(openingBalance) &&
                    NumberUtils.isZero(closingBalance);

                if (isRowEmpty) {
                    return;
                }

                const isBalanceSwitchAccount = rawAccountsHelper.wasPreviousYearBalanceSwitchAccount(accountNumber);
                rawAccounts.push({
                    accountNumber: accountNumber,
                    name: accountName,
                    openingBalance: openingBalance,
                    closingBalance,
                    shareholder,
                    rowNo: index + 1,
                    type: accountType,
                    isBalanceSwitchAccount,
                    purpose,
                });
            }
        });

        // --- Step 2: Summarize accounts that need to be summarized ---
        let accountsForProcessing: ILocalAccount[] = [...rawAccounts];

        // we take respect accounts which are summarized in current year if this was already saved once
        // in this case we ignore what was summarized in previous year and take current year summarization into account
        // because we assume the user has made significant changes.
        // otherwise we try to summarize based on previous year data

        let summarizedAccountsInfo: SummarizedAccountInfo[];

        if (currentSummarizedAccountsSet.size > 0) {
            // For current year, we need to get names from rawAccounts
            summarizedAccountsInfo = Array.from(currentSummarizedAccountsSet).map((accountNumber) => {
                const matchingAccount = rawAccounts.find((acc) => acc.accountNumber === accountNumber);
                return {
                    accountNumber,
                    accountName: matchingAccount?.name ?? '',
                };
            });
        } else {
            // Get both account number and name from previous accounts
            summarizedAccountsInfo = previousAccounts
                .filter((acc: ILocalAccount) => acc.isSummarized)
                .map((acc) => ({
                    accountNumber: acc.accountNumber,
                    accountName: acc.name,
                }));

        }

        if (summarizedAccountsInfo.length > 0) {
            const summarizedAccounts: ILocalAccount[] = [];
            const remainingAccounts: ILocalAccount[] = [];

            // Create a Set for quick lookup and a Map for names
            const summarizedAccountNumbersSet = new Set(summarizedAccountsInfo.map((info) => info.accountNumber));

            summarizedAccountsInfo.forEach(({ accountNumber, accountName }) => {
                const accountsToSummarize = accountsForProcessing.filter(
                    (acc) => acc.accountNumber === accountNumber,
                );
                if (accountsToSummarize.length > 1) {
                    const totalClosingBalance = accountsToSummarize.reduce(
                        (sum, acc) => sum + acc.closingBalance,
                        0,
                    );
                    const totalOpeningBalance = accountsToSummarize.reduce(
                        (sum, acc) => sum + acc.openingBalance,
                        0,
                    );
                    const summarizedAccount: ILocalAccount = {
                        ...accountsToSummarize[0], // Use first account as a template
                        name: accountName || accountsToSummarize[0].name, // Use saved name, fallback to first account's name
                        closingBalance: totalClosingBalance,
                        openingBalance: totalOpeningBalance,
                        isSummarized: true,
                        summarizedFrom: accountsToSummarize,
                    };
                    summarizedAccounts.push(summarizedAccount);
                } else {
                    remainingAccounts.push(...accountsToSummarize);
                }
            });

            const nonSummarizedAccounts = accountsForProcessing.filter(
                (acc) => !summarizedAccountNumbersSet.has(acc.accountNumber),
            );
            accountsForProcessing = [...nonSummarizedAccounts, ...remainingAccounts, ...summarizedAccounts];
        }

        // --- Step 3: Second pass to get previous year values ---
        if (isPreviousYearDataTakenFromPreviousAnnualReport) {
            const previousYearDuplicateAccountNumberSet = this.getPreviousYearDuplicateAccountsSet(previousAccounts);
            const currentYearDuplicateAccountNumberSet = this.getCurrentYearDuplicateAccountsSet(
                foundSheet.rows,
                accountNumberCellIndex,
            );

            rawAccountsHelper.setDuplicateAccountsNumberSets(
                previousYearDuplicateAccountNumberSet,
                currentYearDuplicateAccountNumberSet,
            );
            rawAccountsHelper.setPreviousAccounts(previousAccounts);
            rawAccountsHelper.setFinancialStatementSource(financialStatementSource);

            if (accountType === AccountType.BALANCE) {
                rawAccountsHelper.setPreviousBalanceSwitchAccounts(previousBalanceSwitchAccounts);
            }

            accountsForProcessing.forEach((account) => {
                // --- BEGIN: Logic to populate opening balances for constituent accounts of a summarized account ---
                if (account.isSummarized) {
                    // 1. Find the corresponding summarized account from the previous year 
                    const previousSummarizedAccount = previousAccounts.find(
                        (prevAccount: ILocalAccount) =>
                            prevAccount.accountNumber === account.accountNumber && prevAccount.isSummarized,
                    );

                    if (previousSummarizedAccount) {
                        const currentParts = account.summarizedFrom;
                        const previousParts = (previousSummarizedAccount as ILocalAccount).summarizedFrom || [];

                        const numCurrentParts = currentParts.length;
                        const numPreviousParts = previousParts.length;

                        // If the number of current parts is less than the number of previous parts,
                        // we need at least one current part to hold the sum of the remainder.
                        if (numCurrentParts === 0) {
                            // Edge case: If current summarized account has no parts, we can't assign anything.
                            console.warn(`Summarized account ${account.accountNumber} has no parts in the current year.`);
                            // No action needed, as there's nowhere to assign the balance.
                        } else if (numCurrentParts >= numPreviousParts) {
                            // Case 1: Current structure is equal to or larger than the previous structure.
                            // Transfer balances 1-to-1 using index for all previous parts.
                            previousParts.forEach((previousPart, index) => {
                                const currentPart = currentParts[index];
                                // Check is redundant if numCurrentParts >= numPreviousParts, but safe.
                                if (currentPart) {
                                    currentPart.openingBalance = previousPart.closingBalance ?? 0;
                                }
                            });
                        } else {
                            // Case 2: Current structure is SMALLER than the previous structure (numCurrentParts < numPreviousParts).

                            // A. Transfer balances 1-to-1 for all parts EXCEPT the last current one.
                            // Iterate up to the second-to-last current part (index < numCurrentParts - 1).
                            for (let i = 0; i < numCurrentParts - 1; i++) {
                                const currentPart = currentParts[i];
                                const previousPart = previousParts[i];
                                currentPart.openingBalance = previousPart.closingBalance ?? 0;
                            }

                            // B. Calculate the sum of the remaining previous closing balances.
                            // Start summing from the index corresponding to the second-to-last current part (i = numCurrentParts - 1).
                            let remainderSum = 0;
                            for (let i = numCurrentParts - 1; i < numPreviousParts; i++) {
                                const previousPart = previousParts[i];
                                remainderSum += previousPart.closingBalance ?? 0;
                            }

                            // C. Assign the total sum to the opening balance of the LAST current part.
                            const lastCurrentPartIndex = numCurrentParts - 1;
                            currentParts[lastCurrentPartIndex].openingBalance = remainderSum;
                        }
                    }
                }
                const summarizedFromAccountNumbers = account.summarizedFrom
                // --- END: Logic to populate opening balances ---

                // RETRIEVE ROW FROM MAP
                const row = rowMap.get(account.rowNo);

                const openingBalanceAndClosingBalance = rawAccountsHelper.getAccountOpeningBalanceValue(
                    accountNumberCellIndex,
                    accountOpeningValueCellIndex,
                    accountClosingValueCellIndex,
                    accountOpeningValueCellIndexForDuplicateAccounts,
                    row || { cells: [] }, // Use the specific row, fallback to dummy if not found
                    shareholderCellIndex,
                    account.closingBalance,
                    account.accountNumber, // Pass account number directly
                );

                const comingFromPreviousAnnualReport = !openingBalanceAndClosingBalance.tookFromExcel;

                finalAccounts.push({
                    ...account,
                    openingBalance: openingBalanceAndClosingBalance.openingBalance,
                    closingBalance: openingBalanceAndClosingBalance.closingBalance,
                    comingFromPreviousAnnualReport,
                    summarizedFrom: summarizedFromAccountNumbers,
                });
            });
            // if previous data is taken from previous annual report, then we must also push previous accounts that haven't been uploaded in sheet
            const extraAccountsFromPreviousYear = this.getExtraAccountsFromPreviousYear(
                previousAccounts,
                finalAccounts,
            );
            finalAccounts.push(...extraAccountsFromPreviousYear);
        } else {
            // If previous year data is not taken from the previous report (e.g., it's in a column or not used),
            // we just return the accounts processed from the sheet.
            finalAccounts = accountsForProcessing;
        }


        // Apply Balance Switch Merging Logic
        if (accountType === AccountType.BALANCE) {
            finalAccounts = this.mergeBalanceSwitchAccounts(finalAccounts, previousBalanceSwitchAccounts, previousAccounts);
        }
        return finalAccounts;
    }

    /**
     * Merges duplicate account rows that are identified as Balance Switch accounts.
     * Consolidates opening balances and takes the non-zero value for closing balance.
     */
    private static mergeBalanceSwitchAccounts(
        accounts: IAccount[],
        previousBalanceSwitchAccounts: IBalanceSwitchAccount[],
        previousAccounts: IAccount[] = [],
    ): IAccount[] {
        if (!previousBalanceSwitchAccounts || previousBalanceSwitchAccounts.length === 0) {
            return accounts;
        }

        const balanceSwitchAccountNumbers = new Set(
            previousBalanceSwitchAccounts.map((bs) => bs.accountNumber),
        );
        const previousSwitchByNumber = new Map<string, IBalanceSwitchAccount>(
            previousBalanceSwitchAccounts.map((bs) => [bs.accountNumber, bs]),
        );

        const mergedAccountsMap = new Map<string, IAccount>();
        const nonDuplicateAccounts: IAccount[] = [];
        accounts.forEach((account) => {
            const accNum = account.accountNumber;

            // Only process merging for accounts designated as Balance Switch accounts
            if (balanceSwitchAccountNumbers.has(accNum)) {
                if (mergedAccountsMap.has(accNum)) {
                    const existingAccount = mergedAccountsMap.get(accNum)!;

                    // Merging logic:
                    // 1. take either opening balance, they are the same
                    // 2. Take the non-zero value for closing balance
                    existingAccount.openingBalance = account.openingBalance;
                    existingAccount.isBalanceSwitchAccount = true;

                    if (NumberUtils.isZero(existingAccount.closingBalance) && !NumberUtils.isZero(account.closingBalance)) {
                        existingAccount.closingBalance = account.closingBalance;
                    } else {
                        // take the sum of both closing balances
                        existingAccount.closingBalance += account.closingBalance;
                        console.warn(`Both closing balances for account number ${accNum} are non-zero. Summing them up.`);
                    }

                    // Optionally update name if the existing one is empty
                    if (!existingAccount.name && account.name) {
                        existingAccount.name = account.name;
                    }
                } else {
                    mergedAccountsMap.set(accNum, { ...account });
                }
            } else {
                nonDuplicateAccounts.push(account);
            }
        });

        // BUGFIX (REV-21304, TC07 "Disappears"): a balance-switch account absent from the current upload
        // becomes a PRIOR-YEAR-ONLY ghost. Its carried opening (Vorjahr) must be the previous year's
        // closing, but the ghost is built from the split LEGS whose calculatedClosingBalance is not
        // populated → the merged opening came back null and rendered 0. Recover it from the previous
        // year's data. Prefer reconstructing from the prior year's split LEGS (per-year ground truth) —
        // the summary's originalClosingBalance is frozen at the FIRST split and propagated unchanged
        // across years, so for a 3rd-year ghost it is STALE (would carry the year-1 closing, not year-2's).
        // Fall back to the summary (calculated → raw → stale original) only when the legs are unavailable.
        // Only prior-year-only ghosts are touched, so a normal current-year switch account (which already
        // carries its opening) is unaffected.
        for (const merged of mergedAccountsMap.values()) {
            if (!(merged as { isPreviousYearOnly?: boolean }).isPreviousYearOnly) {
                continue;
            }
            const summary = previousSwitchByNumber.get(merged.accountNumber);
            if (summary == null) {
                continue;
            }
            const fromLegs = BalanceSwitchAccountService.getSignedBalancesFromLegs(summary, previousAccounts);
            const priorClosing =
                fromLegs?.closingBalance ??
                summary.originalClosingBalance ??
                summary.calculatedClosingBalance ??
                summary.closingBalance;
            if (priorClosing != null && NumberUtils.isFinite(priorClosing)) {
                const carried = NumberUtils.roundNumber(priorClosing);
                merged.openingBalance = carried;
                merged.calculatedOpeningBalance = carried;
            }
        }

        return [...nonDuplicateAccounts, ...Array.from(mergedAccountsMap.values())];
    }

    private static getCurrentYearDuplicateAccountsSet(
        rawAccounts: ISheetRow[],
        accountNumberCellIndex: number,
    ): Set<string> {
        const accountNumbers = rawAccounts.map((rawAccount) => {
            // if cell is empty in the sheet of excel the parser will make value t be null
            let accountNumber = rawAccount.cells[accountNumberCellIndex];
            if (accountNumber == null) {
                accountNumber = '';
            }
            return accountNumber;
        });
        const allDuplicateAccountNumber = accountNumbers.filter(
            (accountNumber, index) => accountNumbers.indexOf(accountNumber) !== index,
        );
        return new Set<string>(allDuplicateAccountNumber);
    }

    private static getCurrentYearDuplicateAccountsSetFromParsedAccounts(
        accounts: IAccount[]
    ): Set<string> {
        const accountNumbers = accounts.map((account) => account.accountNumber ?? '');
        const allDuplicateAccountNumber = accountNumbers.filter(
            (accountNumber, index) => accountNumbers.indexOf(accountNumber) !== index,
        );
        return new Set<string>(allDuplicateAccountNumber);
    }

    private static getPreviousYearDuplicateAccountsSet(previousYearAccounts: IAccount[]): Set<string> {
        if (previousYearAccounts == null || previousYearAccounts.length === EMPTY_ARRAY_LENGTH) {
            return new Set<string>();
        }
        const accountNumbers = previousYearAccounts.map((account) => account.accountNumber);
        const allDuplicateAccountNumber = accountNumbers.filter(
            (accountNumber, index) => accountNumbers.indexOf(accountNumber) !== index,
        );
        return new Set<string>(allDuplicateAccountNumber);
    }

    private static getExtraAccountsFromPreviousYear(
        previousYearAccounts: IAccount[],
        currentYearAccounts: IAccount[],
    ): IAccount[] {
        const extraAccountsInPreviousAnnualReport = differenceBy<IAccount, IAccount>(
            previousYearAccounts,
            currentYearAccounts,
            'accountNumber',
        );
        const mappedExtraAccountsInPreviousAnnualReport: IAccount[] = [];

        extraAccountsInPreviousAnnualReport.forEach((account) => {
            const openingBalance = NumberUtils.roundNumber(account.calculatedClosingBalance);
            const ghost: IAccount = {
                accountNumber: account.accountNumber,
                name: account.name,
                rowNo: account.rowNo,
                type: account.type,
                openingBalance,
                closingBalance: 0,
                shareholder: account.shareholder,
                comingFromPreviousAnnualReport: true,
                // REV-18627 (B6): Vorjahr-only-Konto (Geisterkonto) erbt den Zweck seines
                // Vorjahreskontos — die aktuelle Datei kennt es nicht mehr.
                purpose: (account as ILocalAccount).purpose,
            };
            // Mark it as a PRIOR-YEAR-ONLY ghost (absent from the current upload). For balance-switch
            // accounts the carried value must come from the summary, not the split legs (whose
            // calculatedClosingBalance is not populated here) — see mergeBalanceSwitchAccounts.
            (ghost as { isPreviousYearOnly?: boolean }).isPreviousYearOnly = true;
            mappedExtraAccountsInPreviousAnnualReport.push(ghost);
        });

        return mappedExtraAccountsInPreviousAnnualReport;
    }

    private static isWithinRange(range: string, field: string | number | null): boolean {
        if (field == null || range === '') {
            return true;
        }
        const lowerBoundaryString = range.split('-')[LOWER_BOUNDARY_INDEX_AFTER_RANGE_SPLIT];
        const lowerBoundaryNumber = NumberUtils.getIntegerValue(lowerBoundaryString);
        const upperBoundaryString = range.split('-')[UPPER_BOUNDARY_INDEX_AFTER_RANGE_SPLIT];

        let isFieldWithinRange: boolean;
        const fieldLength = field.toString().length;
        if (upperBoundaryString == null) {
            isFieldWithinRange = fieldLength === lowerBoundaryNumber;
        } else {
            const upperBoundaryNumber = NumberUtils.getIntegerValue(upperBoundaryString);
            isFieldWithinRange = fieldLength >= lowerBoundaryNumber && fieldLength <= upperBoundaryNumber;
        }

        return isFieldWithinRange;
    }

    private static hasSubstring(word: string, field: string): boolean {
        if (word === '' || (field && field.includes(word))) {
            return true;
        }
        return false;
    }

    private static hasNoSubstring(word: string, field: string): boolean {
        if (word !== '' && field && field.includes(word)) {
            return false;
        }
        return true;
    }

    private static hasNoZeroBalance(removeAccountWithZeroBalance: boolean, field1: number, field2: number): boolean {
        if (removeAccountWithZeroBalance && NumberUtils.isZero(field1) && NumberUtils.isZero(field2)) {
            return false;
        }
        return true;
    }

    public static getAccountTypeFromFinancialStatementSourceType(
        financialStatementSourceType: FinancialStatementTypes,
    ): AccountType {
        switch (financialStatementSourceType) {
            case FinancialStatementTypes.BALANCE:
                return AccountType.BALANCE;
            case FinancialStatementTypes.INCOME:
                return AccountType.INCOME;
            default:
                throw new Error(`This import definition ${financialStatementSourceType} is not supported`);
        }
    }

    public static rangeCheckValidator(form: AbstractControl): ValidationErrors | null {
        const balanceStartControlValue = form.get('balanceAccountsStartRow')?.value as number | undefined;
        const balanceEndControlValue = form.get('balanceAccountsEndRow')?.value as number | undefined;
        const incomeStartControlValue = form.get('incomeAccountsStartRow')?.value as number | undefined;
        const incomeEndControlValue = form.get('incomeAccountsEndRow')?.value as number | undefined;

        let isBalanceStartValid = true;
        let isBalanceEndValid = true;
        if (balanceEndControlValue != null && balanceStartControlValue != null) {
            isBalanceStartValid = balanceStartControlValue <= balanceEndControlValue;
            isBalanceEndValid = balanceEndControlValue >= balanceStartControlValue;
        }

        let isIncomeStartValid = true;
        let isIncomeEndValid = true;
        if (incomeEndControlValue != null && incomeStartControlValue != null) {
            isIncomeStartValid = incomeStartControlValue <= incomeEndControlValue;
            isIncomeEndValid = incomeEndControlValue >= incomeStartControlValue;
        }

        let isOverlappingRanges = false;
        if (balanceEndControlValue != null && incomeStartControlValue != null) {
            isOverlappingRanges = !(balanceEndControlValue < incomeStartControlValue);
        }

        const invalidBalanceRange = !isBalanceStartValid || !isBalanceEndValid;
        const invalidIncomeRange = !isIncomeStartValid || !isIncomeEndValid;
        const currentFormErrors = cloneDeep<ValidationErrors | null>(form.errors);
        let rangeFormValidationError: ValidationErrors | null = null;

        if (currentFormErrors != null && !invalidBalanceRange && form.getError('invalidBalanceRange') != null) {
            delete currentFormErrors['invalidBalanceRange'];
        }

        if (currentFormErrors != null && !invalidIncomeRange && form.getError('invalidIncomeRange') != null) {
            delete currentFormErrors['invalidIncomeRange'];
        }

        if (currentFormErrors != null && !isOverlappingRanges && form.getError('isOverlappingRanges') != null) {
            delete currentFormErrors['isOverlappingRanges'];
        }
        if (isOverlappingRanges) {
            rangeFormValidationError = { isOverlappingRanges: true };
        }

        if (invalidBalanceRange && invalidIncomeRange) {
            rangeFormValidationError = {
                ...rangeFormValidationError,
                invalidBalanceRange: true,
                invalidIncomeRange: true,
            };
        } else if (invalidBalanceRange) {
            rangeFormValidationError = { ...rangeFormValidationError, invalidBalanceRange: true };
        } else if (invalidIncomeRange) {
            rangeFormValidationError = { ...rangeFormValidationError, invalidIncomeRange: true };
        }
        return { ...rangeFormValidationError, ...currentFormErrors };
    }

    public static getAccountUnmatchableDuplicateAccounts(
        currentAccounts: IAccount[],
        previousAccounts: IAccount[],
        previousYearBalanceSwitchAccounts: IBalanceSwitchAccount[],
        financialStatementSource: IFinancialStatementSource,
        recheck: boolean = false,
    ): { unmatchableAccounts: IAccount[]; newShareholderAccounts: IAccount[] } {
        const rawAccountsHelper = new RawAccountsHelper(false, true, financialStatementSource);
        const previousYearDuplicateAccountNumberSet = this.getPreviousYearDuplicateAccountsSet(previousAccounts);
        const currentYearDuplicateAccountNumberSet =
            this.getCurrentYearDuplicateAccountsSetFromParsedAccounts(currentAccounts);
        rawAccountsHelper.setDuplicateAccountsNumberSets(
            previousYearDuplicateAccountNumberSet,
            currentYearDuplicateAccountNumberSet,
        );
        rawAccountsHelper.setPreviousAccounts(previousAccounts);

        const unmatchableAccounts: IAccount[] = [];
        const newShareholderAccounts: IAccount[] = [];

        currentAccounts.forEach((account) => {
            if (currentYearDuplicateAccountNumberSet.has(account.accountNumber)) {
                if (
                    account.accountNumber === '' &&
                    account.name === '' &&
                    account.closingBalance === 0 &&
                    account.openingBalance == 0
                ) {
                    return;
                }
                const previousYearValueIsZero = account.openingBalance === 0;
                const isShareholderAccount = account.shareholder != null && account.shareholder !== '';
                const isPreviousYearBalanceSwitchAccount = previousYearBalanceSwitchAccounts.some(
                    (bsAccount) => bsAccount.accountNumber === account.accountNumber,
                );

                if (isShareholderAccount) {
                    const existsInPreviousYear = previousAccounts.some(
                        (prevAccount) =>
                            prevAccount.accountNumber === account.accountNumber &&
                            prevAccount.name === account.name &&
                            prevAccount.shareholder === account.shareholder,
                    );

                    if (!existsInPreviousYear) {
                        newShareholderAccounts.push(account);
                    }
                } else if (recheck) {
                    if (!isShareholderAccount && !isPreviousYearBalanceSwitchAccount) {
                        unmatchableAccounts.push(account);
                    }
                } else {
                    if (previousYearValueIsZero && !isShareholderAccount && !isPreviousYearBalanceSwitchAccount) {
                        unmatchableAccounts.push(account);
                    }
                }
            }
        });

        return { unmatchableAccounts, newShareholderAccounts };
    }

    public static getAccountWhichExistsInPreviousYearMoreThanThisYear(
        currentAccounts: IAccount[],
        previousAccounts: IAccount[],
    ): IAccount[] {
        const previousAccountCounts = new Map<string, number>();
        previousAccounts.forEach((acc) => {
            if (acc.isBalanceSwitchAccount) {
                return;
            }
            previousAccountCounts.set(acc.accountNumber, (previousAccountCounts.get(acc.accountNumber) || 0) + 1);
        });

        const currentAccountCounts = new Map<string, number>();
        currentAccounts.forEach((acc) => {
            if (acc.isBalanceSwitchAccount) {
                return;
            }
            currentAccountCounts.set(acc.accountNumber, (currentAccountCounts.get(acc.accountNumber) || 0) + 1);
        });

        const result: IAccount[] = [];
        previousAccountCounts.forEach((count, accountNumber) => {
            const currentCount = currentAccountCounts.get(accountNumber) || 0;
            if (count > currentCount) {
                const accountsToAdd = previousAccounts.filter((acc) => acc.accountNumber === accountNumber);
                result.push(...accountsToAdd);
            }
        });

        return result;
    }
}